// Key management on the Lender Hub API (S-2, api-contract.md §5): the consumers that call the Hub, their scopes, limits
// and keys, and their usage. Shapes: firstfund-contracts/schemas/hub/consumer, consumer-key, consumer-key-made, api-usage.
//   GET /consumers · POST /consumers · PATCH /consumers/{name} · DELETE /consumers/{name}
//   POST /consumers/{name}/keys (the key shown once) · POST /consumers/{name}/keys/{id}/revoke
//   GET /usage?days=1|7|30
import type { z } from 'zod'
import type { ApiUsage as ApiUsageSchema, Consumer as ConsumerSchema, ConsumerKey as ConsumerKeySchema, ConsumerKeyMade as ConsumerKeyMadeSchema, RateLimit as RateLimitSchema, Scope as ScopeSchema } from '@/contract/schemas'
import { api } from '@/lib/hubApi'

export type Scope = z.infer<typeof ScopeSchema>
export type RateLimit = z.infer<typeof RateLimitSchema>
export type ConsumerKey = z.infer<typeof ConsumerKeySchema>
export type ConsumerKeyMade = z.infer<typeof ConsumerKeyMadeSchema>
export type Consumer = z.infer<typeof ConsumerSchema>
export type ApiUsage = z.infer<typeof ApiUsageSchema>
export type UsagePeriod = 1 | 7 | 30

/** Every scope, in order, with what it opens and the header its key goes in. */
export const SCOPES: { id: Scope; label: string; apis: string; header: string }[] = [
  { id: 'catalog', label: 'Catalog', apis: 'GET /service/v1/app/catalog, KFS, logo', header: 'Authorization: Bearer <key>' },
  { id: 'applications', label: 'Applications', apis: 'POST /v1/applications', header: 'x-app-key' },
  { id: 'lender-uat', label: 'Lenders · UAT', apis: 'POST /lenders/{slug}/{route} on the UAT address', header: 'x-hub-key' },
  { id: 'lender-production', label: 'Lenders · production', apis: 'POST /lenders/{slug}/{route} on the production address', header: 'x-hub-key' },
]
export const scopeLabel = (s: Scope) => SCOPES.find((x) => x.id === s)?.label ?? s

export const getConsumers = () => api<Consumer[]>('GET', '/consumers')
export const addConsumer = (body: { name: string; title: string; purpose: string; scopes: Scope[] }) => api<Consumer>('POST', '/consumers', body)
export const updateConsumer = (name: string, body: { title?: string; purpose?: string; active?: boolean; scopes?: Scope[]; limits?: RateLimit[] }) => api<Consumer>('PATCH', `/consumers/${name}`, body)
export const deleteConsumer = (name: string) => api<{ deleted: string }>('DELETE', `/consumers/${name}`)
export const makeKey = (name: string, body: { label: string; expiresAt: string | null }) => api<ConsumerKeyMade>('POST', `/consumers/${name}/keys`, body)
export const revokeKey = (name: string, id: string) => api<ConsumerKey>('POST', `/consumers/${name}/keys/${id}/revoke`, {})
export const getUsage = (days: UsagePeriod) => api<ApiUsage>('GET', `/usage?days=${days}`)

/** A consumer's name from its title: "Partner Bank" → partner-bank. */
export const nameFrom = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/^[^a-z]+/, '').slice(0, 40)
